/**
 * "Módulo X" shortcut detector. Items whose label starts with "Módulo "
 * (case-insensitive) are jump-offs that open the underlying module — they
 * never represent the current page, so they must NOT be highlighted as
 * active nor enter the active-path chain even when their route happens to
 * be a prefix of the current URL by coincidence (e.g. "Módulo Atendimento"
 * → /atendimento → /attendance, while the user is on /attendance/collection-queue).
 */
export function isModuleShortcutLabel(label: string | undefined): boolean {
  if (!label) return false;
  return /^módulo\s/i.test(label.trim());
}

/**
 * Hierarchical menu item (sidebar and lateral flyouts).
 *
 * Can represent any tree level — `children` is recursive. Items without
 * `children` act as leaves and navigate via `route` (or open `externalUrl`
 * in a new tab). Items with children are "folders" and open the next-level
 * flyout when clicked, even if they have their own `route`.
 */
export interface MenuItem {
  /** Stable identifier for trackBy and active-path computation. */
  id?: string | number;
  /** Display text. When `labelKey` is set, the translation takes precedence. */
  label: string;
  /** Preferred i18n key (overrides `label` when present). */
  labelKey?: string;
  /** Internal Angular route. Followed only when the item has NO children. */
  route?: string;
  /** External URL opened in a new tab (also leaf-only). */
  externalUrl?: string;
  /** Lucide icon name already resolved by `backendIconToLucide`. */
  iconSvg?: string;
  /** Children for the next flyout level. */
  children?: MenuItem[];
  /** Backend module link (preserved for the quick-access integration). */
  moduleId?: number;
  /** Backend submodule link (leaf). */
  submoduleId?: number;
  /** Stable backend key (MODULO_KEY/SUBMODULO_KEY). Idioma-agnóstica; usada para divider antes do item 'comunicacao'. */
  key?: string;
  /** When set, leaf opens this URL template in a new tab (after `{userEmail}` substitution). */
  externalLaunchUrlTemplate?: string | null;
}

/**
 * A leaf activation, as the gesture that produced it describes itself.
 *
 * The gesture is carried rather than inferred at the far end because only the
 * gesture knows — the same reason the cascade's own openings declare whose they
 * are. By the time the sidebar handles the activation, `document.activeElement`
 * is the menu item under either gesture (a click focuses the button it lands
 * on), so nothing downstream can tell a keypress from a click.
 *
 * It has to be told apart: activating a leaf **by keyboard** is inside the
 * focus contract and ends on the destination's content region (AC19, D15),
 * while selection **by mouse** is explicitly outside it and leaves the focus
 * exactly where it is today (D7).
 */
export interface MenuLeafActivation {
  readonly item: MenuItem;
  readonly fromKeyboard: boolean;
}

/**
 * Stable identity of a menu item, as a string.
 *
 * `id` is built from the backend key (`module:${key}` / `submodule:${key}`), so
 * it is language-agnostic and identical across re-renders — unlike the item
 * OBJECT, which `applyMenu` rebuilds from the DTOs on every emission of the
 * tree. Anything that has to recognise "the same item" after a re-render
 * compares this, never the reference (cause C-04, TASK-61043).
 *
 * Falls back to the label for the handful of hand-built items that carry no
 * `id`.
 *
 * **This is the rule the cascade resolves paths by, and on that axis it has to
 * be the ONLY one.** The axis is `data-item-id`, `trackBy` and every key path
 * built out of them: the sidebar and the flyout publish it on `data-item-id`
 * and track by it, and `MenuFocusService` reads a focused item's path off that
 * very attribute — so the day the attribute is written with an expression of
 * its own instead of through this function, the two drift, every key path stops
 * matching, and the recovery fires on every re-render. The templates therefore
 * bind `itemKey(item)`, an alias of this function that a template can call.
 *
 * `activePath` is keyed apart from all of that and is NOT this rule: it is a
 * set of raw `item.id ?? item.label` values, unstringified, compared only
 * against itself to decide highlighting. Nothing resolves a path through it and
 * nothing reads it off the DOM, so its spellings are not drift against this
 * one — they are a second, self-contained axis.
 *
 * `menu-drawer.component.ts` deliberately keys items by ANOTHER rule: it is the
 * mobile drawer, it renders no cascade and no `data-item-id`, and it takes no
 * part in resolution by path.
 */
export function menuItemKey(item: MenuItem): string {
  return String(item.id ?? item.label);
}

/** The item `key` names in `items`, whether or not it can host a panel. */
export function findByKey(items: MenuItem[], key: string): MenuItem | null {
  return items.find(item => menuItemKey(item) === key) ?? null;
}

/**
 * The item `key` names in `items`, kept only while it can still host a panel.
 *
 * The single rule for "this level stays open". The sidebar re-points the open
 * cascade with it, the flyout re-points its open child with it, and the
 * sidebar's own "is the focused path still rendered" walk asks it about every
 * segment above the deepest — so what a re-render leaves mounted and what the
 * focus recovery believes is mounted are the same answer, not two that happen
 * to agree today.
 */
export function findOpenableChild(items: MenuItem[], key: string): MenuItem | null {
  const item = findByKey(items, key);
  return item?.children?.length ? item : null;
}

/**
 * A folder whose visible child set came back EMPTY.
 *
 * The mapping distinguishes the two on purpose: an item that never had children
 * carries no `children` at all, and one whose children were every one of them
 * denied by the permission check carries an empty array. So this shape is not
 * "a leaf" — it is a folder with nothing left to show, and the keyboard owes it
 * nothing: no panel (there is none to mount) and no activation (there is no
 * route to follow). Activating it as a leaf is what closed the whole cascade
 * and left the keyboard on a button that had just been removed (AC11, AC4).
 *
 * Agrees with `findOpenableChild` by construction — an item this returns `true`
 * for can never host a panel, so what a re-render leaves mounted and what the
 * keyboard treats as openable stay the same answer.
 */
export function isEmptiedFolder(item: MenuItem): boolean {
  return !!item.children && item.children.length === 0;
}
